import { Link } from "react-router-dom";
import { UserPlus, Rocket, CreditCard, Sparkles, ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LangContext";

type Step = {
  n: string;
  icon: React.ComponentType<{ className?: string }>;
  title: { en: string; bn: string };
  body: { en: string; bn: string };
  tint: string; // pastel wash
  ink: string; // accent for number
};

const STEPS: Step[] = [
  {
    n: "01",
    icon: UserPlus,
    title: { en: "Create your account", bn: "অ্যাকাউন্ট খুলুন" },
    body: {
      en: "Sign up in under 3 minutes with your BMDC number — no card required.",
      bn: "৩ মিনিটে সাইন আপ করুন, BMDC নম্বর দিয়ে — কার্ড লাগবে না।",
    },
    tint: "#e6faf3",
    ink: "#1ba179",
  },
  {
    n: "02",
    icon: Rocket,
    title: { en: "Explore free for 14 days", bn: "১৪ দিন ফ্রি ব্যবহার করুন" },
    body: {
      en: "Full access to every feature on the Pro plan — prescriptions, AI scribe, billing.",
      bn: "প্রো প্ল্যানের সব ফিচার — প্রেসক্রিপশন, AI স্ক্রাইব, বিলিং।",
    },
    tint: "#f0fdf4",
    ink: "#16a34a",
  },
  {
    n: "03",
    icon: CreditCard,
    title: { en: "Pick a plan that fits", bn: "প্ল্যান বেছে নিন" },
    body: {
      en: "Pay in BDT via bKash, Nagad, or card. Cancel anytime, no lock-in.",
      bn: "বিকাশ, নগদ বা কার্ডে টাকায় পেমেন্ট। যেকোনো সময় বাতিল।",
    },
    tint: "#fefce8",
    ink: "#ca8a04",
  },
  {
    n: "04",
    icon: Sparkles,
    title: { en: "Customize & grow", bn: "কাস্টমাইজ করে এগিয়ে যান" },
    body: {
      en: "Add chambers, staff, and integrations as your practice scales.",
      bn: "চেম্বার, স্টাফ ও ইন্টিগ্রেশন যোগ করুন প্র্যাকটিসের সাথে।",
    },
    tint: "#faf5ff",
    ink: "#7c3aed",
  },
];

export default function FourStepsSection() {
  const { lang } = useLang();
  const isBn = lang === "bn";
  const displayFont = isBn
    ? "'Noto Sans Bengali', 'Plus Jakarta Sans', system-ui, sans-serif"
    : "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif";

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#1ba179] px-3 py-1.5 rounded-full bg-[#e6faf3] border border-[#c7f1e2]">
          {isBn ? "কীভাবে শুরু করবেন" : "Get started"}
        </div>
        <h2
          className="mt-4 text-3xl sm:text-4xl md:text-5xl font-bold text-[#0f172a] tracking-tight"
          style={{ fontFamily: displayFont }}
        >
          {isBn ? "৪ ধাপে শুরু হোক আপনার ডিজিটাল চেম্বার" : "Four simple steps to get started"}
        </h2>
        <p className="mt-4 text-[15px] text-[#475569]">
          {isBn
            ? "আধা ঘণ্টার মধ্যে পুরো চেম্বার অনলাইনে — সহজ, নিরাপদ, বাংলায়।"
            : "Set up your entire practice in under an hour. No installs, no training required."}
        </p>
      </div>

      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map(({ n, icon: Icon, title, body, tint, ink }) => (
          <div
            key={n}
            className="relative rounded-3xl border border-[#e2e8f0] bg-white p-6 transition-shadow hover:shadow-lg"
          >
            {/* Illustration tile */}
            <div
              className="relative h-32 rounded-2xl flex items-center justify-center overflow-hidden"
              style={{ background: tint }}
            >
              <span
                className="absolute top-3 left-4 text-[52px] leading-none font-black tracking-tighter select-none"
                style={{ color: ink, opacity: 0.18, fontFamily: displayFont }}
              >
                {n}
              </span>
              <div
                className="relative w-14 h-14 rounded-2xl flex items-center justify-center shadow-md"
                style={{ background: "white", color: ink, border: `1px solid ${ink}22` }}
              >
                <Icon className="w-6 h-6" />
              </div>
              <span
                className="absolute bottom-2 right-3 text-[10px] font-bold uppercase tracking-[0.16em]"
                style={{ color: ink }}
              >
                {isBn ? "ধাপ" : "Step"} {n}
              </span>
            </div>

            <h3
              className="mt-5 text-[17px] font-bold text-[#0f172a] leading-snug"
              style={{ fontFamily: displayFont }}
            >
              {isBn ? title.bn : title.en}
            </h3>
            <p className="mt-2 text-[13.5px] text-[#475569] leading-relaxed">
              {isBn ? body.bn : body.en}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-10 flex justify-center">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 bg-[#0f172a] hover:bg-[#1e293b] text-white text-[14px] font-semibold px-6 py-3.5 rounded-full transition-colors"
        >
          {isBn ? "প্ল্যান ও দাম দেখুন" : "See plans and pricing"}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}
